import { supabaseAuthProvider } from 'ra-supabase';
import { AuthProvider, UserIdentity } from 'react-admin';
import { supabase } from './supabase';
import { getProfile, OrganizationId, Profile } from '@wwf/sdk';
import { getFullName } from '@domain/member';

export async function getIdentity(): Promise<UserIdentity> {
    const profile = await getProfileFromCache();

    if (profile == null) {
        throw new Error();
    }

    return {
        id: profile.id,
        fullName: getFullName(profile),
        avatar: undefined,
        profile,
    } as UserIdentity;
}

const baseAuthProvider = supabaseAuthProvider(supabase, {
    getIdentity,
});

export async function getIsInitialized() {
    if (getIsInitialized._is_initialized_cache == null) {
        getIsInitialized._is_initialized_cache = true;
    }

    return getIsInitialized._is_initialized_cache;
}

export namespace getIsInitialized {
    export var _is_initialized_cache: boolean | null = null;
}

export const authProvider: AuthProvider = {
    ...baseAuthProvider,
    login: async params => {
        profileCacheState.profileCache = undefined;
        profileCacheState.profileCachePromise = null;
        const result = await baseAuthProvider.login(params);
        return result;
    },
    logout: async params => {
        const result = await baseAuthProvider.logout(params);
        profileCacheState.profileCache = undefined;
        profileCacheState.profileCachePromise = null;
        return result;
    },
    checkAuth: async params => {
        // Users are on the set-password page, nothing to do
        if (
            window.location.pathname === '/set-password' ||
            window.location.hash.includes('#/set-password')
        ) {
            return;
        }
        // Users are on the forgot-password page, nothing to do
        if (
            window.location.pathname === '/forgot-password' ||
            window.location.hash.includes('#/forgot-password')
        ) {
            return;
        }
        // Users are on the sign-up page, nothing to do
        if (
            window.location.pathname === '/sign-up' ||
            window.location.hash.includes('#/sign-up')
        ) {
            return;
        }

        const isInitialized = await getIsInitialized();

        if (!isInitialized) {
            await supabase.auth.signOut();
            // eslint-disable-next-line no-throw-literal
            throw {
                redirectTo: '/sign-up',
                message: false,
            };
        }

        return baseAuthProvider.checkAuth(params);
    },
    canAccess: async () => {
        const isInitialized = await getIsInitialized();
        if (!isInitialized) return false;

        const profile = await getProfileFromCache();
        if (profile == null) return false;

        return profile.type === 'STAFF';
    },
    checkError: async error => {
        if (error?.status === 403) {
            return;
        }
        return baseAuthProvider.checkError(error);
    },
};

type ProfileCacheState = {
    profileCache: Profile | null | undefined;
    profileCachePromise: Promise<Profile | null> | null;
};

function getProfileCacheState(): ProfileCacheState {
    const g = globalThis as unknown as {
        __wwf_profileCacheState?: ProfileCacheState;
    };

    if (!g.__wwf_profileCacheState) {
        g.__wwf_profileCacheState = {
            profileCache: undefined,
            profileCachePromise: null,
        };
    }

    return g.__wwf_profileCacheState;
}

const profileCacheState = getProfileCacheState();

const getProfileFromCache = async (): Promise<Profile | null> => {
    if (profileCacheState.profileCache !== undefined) {
        return profileCacheState.profileCache;
    }

    if (profileCacheState.profileCachePromise) {
        return await profileCacheState.profileCachePromise;
    }

    profileCacheState.profileCachePromise = (async () => {
        try {
            const profile = await getProfile();
            profileCacheState.profileCache = profile ?? null;
            return profileCacheState.profileCache;
        } catch (e) {
            console.error('getProfileFromCache.error', e);
            profileCacheState.profileCache = null;
            return null;
        }
    })();

    try {
        return await profileCacheState.profileCachePromise;
    } finally {
        profileCacheState.profileCachePromise = null;
    }
};

export async function resolveStaffOrganizationId(): Promise<OrganizationId> {
    const profile = await getProfileFromCache();
    const id = profile?.organizationId;
    if (!id) {
        throw new Error(
            'Current user has no organization; cannot save tenant-scoped records.'
        );
    }
    return id;
}
